import React from 'react';
import { Body } from '@sato/components';
import { observer } from 'mobx-react-lite';

import ServicePartyInfo from '@components/common/ServicePartyInfo';
import { useApartmentProperties } from '@hooks/useApartmentProperties';
import { apartmentStore } from '@stores/apartmentStore';
import { t } from '@stores/localization';

/**
 * Displays ServicePartyInfo of the service party that handles sauna reservations
 *
 * ## Rules for showing sauna reservation party: ##
 *
 * - party (reservation party) = yes, system (reservation system) = yes:
 * -> Show service party
 *
 * - party = yes, system = no
 * -> Show service party
 *
 * - party = no, system = yes
 * -> Do not show service party
 *
 * - party = no, system = no
 * -> Show maintenance company service party
 *
 * @link https://satocorporation.atlassian.net/browse/JKB-8351
 */
export const SaunaReservationServiceParty = observer(() => {
  const { housingManager, maintenanceCompany } = useApartmentProperties();

  const realEstateSauna =
    apartmentStore.selectedApartment?.realestate.realEstateFeatureLocations.find(
      (location) => location.feature === 'REAL_ESTATE_SAUNA'
    );

  if (!realEstateSauna) return null;

  // Resolve sauna reservation party
  const saunaReservationParty = (() => {
    // Default to maintenance company if neither reservation party nor system are defined.
    if (
      !realEstateSauna.reservation?.reservationSystem &&
      !realEstateSauna.reservation?.reservationParty
    ) {
      return maintenanceCompany;
    }

    switch (realEstateSauna.reservation?.reservationParty) {
      case 'HOSTING_COMPANY':
        return housingManager;

      case 'MAINTENANCE_COMPANY':
        return maintenanceCompany;

      default:
        return null;
    }
  })();

  if (!saunaReservationParty) return null;

  return (
    <div>
      <Body className="pb-3">{t('saunaReservationPartyText')}</Body>

      <ServicePartyInfo serviceParty={saunaReservationParty} />
    </div>
  );
});
